// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from 'react';
import './DeliveryOverview.scss';
import { storage } from '../context/firebase';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { isMobileOnly, MobileOnlyView } from 'react-device-detect';

import PropertyField from '../components/PropertyField/PropertyField';
import BaleCard from '../components/BaleCard/BaleCard';
import ImageSquare from '../shared_components/ImageSquare/ImageSquare';
import MaterialDropdown from '../components/MaterialDropdown/MaterialDropdown';
import DocumentScanner from '../components/DocumentScanner/DocumentScanner';
import Claim from '../components/Claim/Claim';
import SupplierPropertyField from '../components/PropertyField/SupplierPropertyField';
import StockFieldPropertyField from '../components/PropertyField/StockFieldPropertyField';
import DeliveryAnalysis from '../components/DeliveryAnalysis/DeliveryAnalysis';
import { FlaskConical } from 'lucide-react';

// Importing icons
import back_icon from '../assets/icons/white/back_small.png';
import supplier_icon from '../assets/icons/white/delivery.png';
import date_icon from '../assets/icons/white/calendar.png';
import document_icon from '../assets/icons/white/document.png';
import user_icon from '../assets/icons/white/user.png';
import label_icon from '../assets/icons/white/label.png';
import location_icon from '../assets/icons/white/location.png';
import bales_icon from '../assets/icons/white/bales_stacked.png';
import shapes_icon from '../assets/icons/white/shape.png';
import piechart_icon from '../assets/icons/white/piechart.png';
import color_icon from '../assets/icons/white/color.png';
import alert_on_icon from '../assets/icons/red/alert.png';
import { DELIVERIES_ROUTE, DELIVERY_ROUTE } from '../constant/routes';
import { useAuthStore } from '../stores/authStore';
import {
  getLabelDisplayNameMatchingGroup,
  getLabelIdMatchingGroup,
  getLabelsetDisplayName,
  getLabelDisplayName,
} from '../shared_components/utils/FirebaseLabelsetsUtils';
import CommonModal from '../shared_components/CommonModal/CommonModal';

const iconMapping = {
  notes: document_icon,
  user: user_icon,
  label: label_icon,
  location: location_icon,
  bales: bales_icon,
  shapes: shapes_icon,
  piechart: piechart_icon,
  color: color_icon,
};

export default function Delivery({
  handleDeliveryFieldChange,
  deleteDelivery,
  deliveryId,
  delivery,
  showDeliveryImage,
  showBale,
  deliveriesPageData = {},
}) {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const globalLabelsets = useAuthStore((state) => state.globalLabelsets);
  const suppliers = useAuthStore((state) => state.suppliers);
  const claimsEnabled = useAuthStore((state) => state.deliverySettings?.features?.claims);
  const aiVisualizationEnabled = useAuthStore((state) => state.deliverySettings?.features?.ai_visualization);
  const customDeliveryFields = useAuthStore((state) => state.deliverySettings?.custom_delivery_fields);
  const customLabelsetFields = useAuthStore((state) => state.deliverySettings?.custom_labelset_fields);
  const [showProperties, setShowProperties] = React.useState(!isMobileOnly);
  const [showCompletePopup, setShowCompletePopup] = React.useState(false);
  const [showDeletePopup, setShowDeletePopup] = React.useState(false);
  const [showDocumentScannerPopup, setShowDocumentScannerPopup] = React.useState(false);
  const [showClaimPopup, setShowClaimPopup] = React.useState(false);
  const [isAiAnalysisExpanded, setIsAiAnalysisExpanded] = React.useState(false);

  function handleMaterialPropertyChange(labelsetId, value) {
    handleDeliveryFieldChange({
      target: {
        name: 'material_properties',
        value: {
          [labelsetId]: value,
        },
      },
    });
  }

  async function returnToDeliveriesOverview() {
    navigate(DELIVERIES_ROUTE, {
      state: {
        deliveriesPageData: {
          deliveries: deliveriesPageData.deliveries,
          positions: deliveriesPageData.scroll,
          allLoaded: deliveriesPageData.allLoaded,
          lastSnapshot: deliveriesPageData.lastSnapshot,
        },
      },
    });
  }

  async function completeDelivery() {
    // Mark delivery as completed and return back to deliveries page
    handleDeliveryFieldChange({ target: { name: 'delivery_input_completed', value: true } });
    returnToDeliveriesOverview();
  }

  function onSaveButtonClick() {
    if (delivery.delivery_input_completed) {
      returnToDeliveriesOverview();
    } else {
      setShowCompletePopup(true);
    }
  }

  /* Show the delivery-overview page */
  return (
    <div className='delivery'>
      <div className='delivery--header'>
        <div onClick={returnToDeliveriesOverview} className='delivery--back'>
          <img src={back_icon} alt='Back' className='delivery--back-icon' />
        </div>
        <h3 className='delivery--title'>
          {t('delivery')} #{deliveryId}
        </h3>
        <div className='delivery--header-buttons'>
          {claimsEnabled && (
            <button onClick={() => setShowClaimPopup(true)} className='delivery--claim-button'>
              {t('claim')}
            </button>
          )}
          <button onClick={onSaveButtonClick} className='delivery--save'>
            {t('general_save')}
          </button>
          <button onClick={() => setShowDeletePopup(true)} className='delivery--delete'>
            {t('general_delete')}
          </button>
        </div>
      </div>

      <div className='delivery--content'>
        <div
          className={`delivery--content-properties-analysis-container ${isAiAnalysisExpanded || showProperties ? 'expanded' : 'collapsed'}`}
        >
          <div
            className={`delivery--content-properties-container ${isAiAnalysisExpanded && isMobileOnly ? 'hide' : ''}`}
          >
            <MobileOnlyView
              className='delivery--properties-collapser'
              onClick={() => setShowProperties(!showProperties)}
            >
              <div className='delivery--properties-collapser-content'>
                <img src={piechart_icon} />
                <p className='delivery--properties-collapser-content-title'>{t('property_material')}</p>
                <p className='delivery--properties-collapser-content-value'>
                  {getLabelDisplayNameMatchingGroup(
                    delivery.delivery_labels,
                    globalLabelsets,
                    'materials',
                    i18n.language,
                  )}
                </p>
              </div>
              <div className='delivery--properties-collapser-content'>
                <img src={supplier_icon} />
                <p className='delivery--properties-collapser-content-title'>{t('property_supplier')}</p>
                <p className='delivery--properties-collapser-content-value'>
                  {suppliers?.[delivery.supplier_id]?.display_name}
                </p>
              </div>
              <div className='collapse-icon'>
                <img
                  src={back_icon}
                  alt='Expand'
                  className={`delivery--ai-chevron-icon ${showProperties ? 'expanded' : ''}`}
                />
              </div>
            </MobileOnlyView>
            {showProperties && (
              <div className={`delivery--properties ${isMobileOnly ? 'mobile' : ''}`}>
                <PropertyField
                  icon={document_icon}
                  title={t('property_notes')}
                  value={delivery.notes}
                  name='notes'
                  type='textarea'
                  handleChange={handleDeliveryFieldChange}
                  editable={true}
                  grid_column={isMobileOnly ? '1' : '2 / span 2'}
                  grid_row={isMobileOnly ? '1 / span 2' : '2 / span 2'}
                />
                <MaterialDropdown
                  name='materials'
                  value={getLabelIdMatchingGroup(delivery.delivery_labels, globalLabelsets, 'materials')}
                  handleChange={handleDeliveryFieldChange}
                  splitProductGroup={true}
                />
                <SupplierPropertyField
                  value={delivery.supplier_id}
                  handleDeliveryFieldChange={handleDeliveryFieldChange}
                />
                <PropertyField
                  icon={document_icon}
                  title={t('property_document_scanner')}
                  value={
                    Object.keys(delivery.documents_data?.scanned_documents ?? {}).length > 0
                      ? `${Object.keys(delivery.documents_data?.scanned_documents ?? {}).length} ${t('delivery_documents_scanned')}`
                      : ''
                  }
                  name={'slip_nr'}
                  type={null}
                  handleChange={handleDeliveryFieldChange}
                  editable={false}
                  onClick={() => {
                    setShowDocumentScannerPopup(true);
                  }}
                />
                <StockFieldPropertyField
                  value={delivery.storage_field}
                  handleDeliveryFieldChange={handleDeliveryFieldChange}
                />
                {Object.entries(customDeliveryFields ?? {}).map(([key, value], index) => {
                  return (
                    <PropertyField
                      key={index}
                      icon={iconMapping[value.icon]}
                      title={value.title}
                      value={delivery?.[key]}
                      name={key}
                      type={value.type}
                      handleChange={handleDeliveryFieldChange}
                      editable={true}
                    />
                  );
                })}
                <PropertyField
                  icon={date_icon}
                  title={t('property_date')}
                  value={delivery.date}
                  name='date'
                  type='datetime-local'
                  handleChange={handleDeliveryFieldChange}
                  editable={true}
                />
                <PropertyField
                  icon={user_icon}
                  title={t('property_submitted_by')}
                  value={delivery.delivery_submitted_by_user}
                  name={'delivery_submitted_by_user'}
                  type='select-extendible'
                  handleChange={handleDeliveryFieldChange}
                  editable={true}
                />
                {/* Custom Labelset Fields */}
                {(() => {
                  const validLabelsets = Object.entries(customLabelsetFields ?? {}).filter(
                    ([labelsetId]) => globalLabelsets?.[labelsetId],
                  );

                  if (validLabelsets.length === 0) return null;

                  return (
                    <>
                      {isMobileOnly && (
                        <div className='delivery--properties-separator'>
                          <span>{t('material_properties')}</span>
                        </div>
                      )}
                      {validLabelsets.map(([labelsetId], index) => {
                        const labelset = globalLabelsets?.[labelsetId];
                        const displayName = getLabelsetDisplayName(labelset, i18n.language).toUpperCase();

                        // Determine field type and build options for single-choice labelsets
                        let fieldType = 'text';
                        let fieldOptions = {};
                        let fieldUnit = undefined;

                        if (labelset.type === 'number') {
                          fieldType = 'number';
                          fieldUnit = labelset?.labels?.unit;
                        } else if (
                          labelset.type === 'single-choice' ||
                          labelset.type === 'ordinal' ||
                          labelset.type === 'multiple-choice'
                        ) {
                          fieldType = labelset.type === 'multiple-choice' ? 'multiple-choice-select' : 'select';
                          // Build options object from labelset labels
                          fieldOptions = Object.keys(labelset.labels || {}).reduce((acc, labelId) => {
                            acc[labelId] = getLabelDisplayName(labelset, labelId, i18n.language);
                            return acc;
                          }, {});
                        }

                        return (
                          <PropertyField
                            key={`labelset-${index}`}
                            icon={<FlaskConical />}
                            title={displayName}
                            value={delivery?.material_properties?.[labelsetId]}
                            name={labelsetId}
                            type={fieldType}
                            options={fieldOptions}
                            unit={fieldUnit}
                            handleChange={(event) => handleMaterialPropertyChange(labelsetId, event.target.value)}
                            editable={true}
                          />
                        );
                      })}
                    </>
                  );
                })()}
              </div>
            )}
          </div>

          {/* AI Analysis Section */}
          {aiVisualizationEnabled && (
            <div className={`delivery--content-analysis-container ${showProperties && isMobileOnly ? 'hide' : ''}`}>
              <DeliveryAnalysis
                delivery={delivery}
                deliveryId={deliveryId}
                showBale={showBale}
                showDeliveryImage={showDeliveryImage}
                hide={showProperties && isMobileOnly}
                onShowAiAnalysisChange={setIsAiAnalysisExpanded}
              />
            </div>
          )}
        </div>

        <div className={`delivery--images-bales-group ${isMobileOnly && showProperties ? 'hide' : ''}`}>
          <div className='delivery--bales-group'>
            <div className='delivery--bales-header'>
              <h3 className='delivery--bales-title'>{t('delivery_images_delivery')}</h3>
            </div>
            <div className='delivery--image-box'>
              <div className={isMobileOnly ? 'grid-card-wrapper' : 'horizontal-scroll-card-wrapper'}>
                <div
                  className='delivery--delivery-images-card-new'
                  onClick={() => navigate(DELIVERY_ROUTE, { state: { delivery_id: deliveryId, mode: 'scanning' } })}
                >
                  <p>+</p>
                </div>
                {Object.entries(delivery.delivery_images || {})
                  .sort()
                  .map(([deliveryImageId, deliveryImage]) => {
                    return (
                      <div
                        onClick={() => {
                          showDeliveryImage(deliveryImageId);
                        }}
                        key={deliveryImageId}
                      >
                        <ImageSquare
                          key={deliveryImageId}
                          storage={storage}
                          imageStoragePath={deliveryImage.image_storage_path}
                          icons={deliveryImage?.flagged ? [alert_on_icon] : []}
                          size={128}
                        />
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>

          <div className='delivery--bales-group'>
            <div className='delivery--bales-header'>
              <h3 className='delivery--bales-title'>{t('delivery_images_bales')}</h3>
              <p className='delivery--bales-count'>
                {Object.keys(delivery.bales || {}).length} {t('delivery_bales_detected')}
              </p>
            </div>
            <div className='delivery--image-box'>
              <div className='grid-card-wrapper'>
                {Object.entries(delivery.bales || {})
                  .filter(([, bale]) => bale?.image_storage_path) // Filter out bales without image_storage_path
                  .sort((a, b) => {
                    return a[1]?.flagged && !b[1]?.flagged
                      ? -1
                      : !a[1]?.flagged && b[1]?.flagged
                        ? 1
                        : a[0].localeCompare(b[0]);
                  })
                  .map(([baleId, bale]) => {
                    return (
                      <div
                        onClick={() => {
                          showBale(baleId);
                        }}
                        key={baleId}
                      >
                        <BaleCard
                          key={baleId}
                          imageStoragePath={bale.image_storage_path}
                          flagged={bale.flagged}
                          baleId={baleId}
                          deliveryId={deliveryId}
                        />
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirm Delivery Complete Popup Modal */}
      <CommonModal
        show={showCompletePopup}
        title={t('delivery_confirm_complete')}
        confirmText={t('complete_delivery_complete')}
        cancelText={t('complete_delivery_save_draft')}
        onConfirm={completeDelivery}
        onCancel={returnToDeliveriesOverview}
        theme='dark'
        topCloseButton
        containerClassName='text-center !max-w-[27.5rem]'
      >
        {t('delivery_confirm_complete_info')}
      </CommonModal>

      {/* Delete Delivery Popup Modal */}
      <CommonModal
        show={showDeletePopup}
        title={t('delivery_confirm_delete')}
        confirmText={t('general_delete')}
        cancelText={t('general_cancel')}
        onConfirm={deleteDelivery}
        onCancel={() => setShowDeletePopup(false)}
        theme='dark'
        variant='danger'
        containerClassName='text-center'
        topCloseButton
      >
        {t('delivery_confirm_delete_info')}
      </CommonModal>

      {/* Document Scanner Popup Modal */}
      <DocumentScanner
        show={showDocumentScannerPopup}
        onHide={() => setShowDocumentScannerPopup(false)}
        documentsData={delivery?.documents_data}
        deliveryId={deliveryId}
        handleDocumentsDataFieldChange={(data) => {
          handleDeliveryFieldChange({
            target: { name: 'documents_data', value: data },
          });
        }}
      />

      {/* Claim Popup Modal */}
      <Claim
        delivery={delivery}
        show={showClaimPopup}
        onHide={() => setShowClaimPopup(false)}
        handleDeliveryFieldChange={handleDeliveryFieldChange}
        showBale={showBale}
        showDeliveryImage={showDeliveryImage}
      />
    </div>
  );
}
